import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SidebarItem {
  id: string;
  title: string;
  href?: string;
  icon?: keyof typeof Icons;
  badge?: React.ReactNode;
  active?: boolean;
  children?: SidebarItem[];
  expanded?: boolean;
}

export interface SidebarSection {
  title?: string;
  items: SidebarItem[];
}

export interface SidebarProps {
  logo?: React.ReactNode;
  sections?: SidebarSection[];
  collapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  className?: string;
  version?: string;
}

export function Sidebar({
  logo,
  sections = [],
  collapsed: controlledCollapsed,
  onCollapseChange,
  className,
  version
}: SidebarProps) {
  const location = useLocation();
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  
  const collapsed = controlledCollapsed !== undefined ? controlledCollapsed : internalCollapsed;
  const setCollapsed = (value: boolean) => {
    if (controlledCollapsed === undefined) {
      setInternalCollapsed(value);
    }
    onCollapseChange?.(value);
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const isActive = (href: string) => {
    // Strict exact match for all items — prevents sibling routes from highlighting
    return location.pathname === href;
  };

  const isItemOrChildActive = (item: SidebarItem): boolean => {
    if (item.href && isActive(item.href)) return true;
    if (item.children) {
      return item.children.some(child => child.href && isActive(child.href));
    }
    return false;
  };

  const defaultLogo = (
    <Link to="/" className="flex items-center gap-3">
      <div className="w-8 h-8 rounded bg-tf-primary flex items-center justify-center flex-shrink-0">
        <Icons.Zap className="w-5 h-5 text-white" />
      </div>
      {!collapsed && (
        <span className="text-base font-semibold text-tf-text">
          NextStore
        </span>
      )}
    </Link>
  );

  return (
    <aside
      className={cn(
        "bg-tf-bg-hover border-r border-tf-border transition-all duration-300 flex flex-col",
        collapsed ? "w-16" : "w-64",
        className
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          "h-14 border-b border-tf-border flex items-center",
          collapsed ? "px-3 justify-center" : "px-4"
        )}
      >
        {logo || defaultLogo}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-4">
            {/* Section Title */}
            {!collapsed && section.title && (
              <div className="px-3 mb-1.5">
                <h3 className="text-xs font-semibold text-tf-text-sub uppercase tracking-wide">
                  {section.title}
                </h3>
              </div>
            )}

            {/* Section Items */}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const IconComponent = item.icon ? (Icons as any)[item.icon] : Icons.Circle;
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.id);
                const active = item.active !== undefined ? item.active : (item.href ? isActive(item.href) : isItemOrChildActive(item));
                
                return (
                  <div key={item.id}>
                    {/* Parent Item */}
                    {hasChildren ? (
                      <button
                        onClick={() => toggleExpanded(item.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded transition-all text-sm font-medium relative",
                          active
                            ? "bg-tf-primary text-white shadow-sm"
                            : "text-tf-text hover:bg-tf-bg-pressed"
                        )}
                      >
                        <IconComponent className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate text-left">{item.title}</span>
                            <Icons.ChevronDown 
                              className={cn(
                                "w-4 h-4 transition-transform",
                                isExpanded && "rotate-180"
                              )}
                            />
                          </>
                        )}
                      </button>
                    ) : (
                      <Link
                        to={item.href || '#'}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 rounded transition-all text-sm font-medium relative",
                          active
                            ? "bg-tf-primary text-white shadow-sm"
                            : "text-tf-text hover:bg-tf-bg-pressed"
                        )}
                      >
                        <IconComponent className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate">{item.title}</span>
                            {item.badge && <span className="flex-shrink-0">{item.badge}</span>}
                          </>
                        )}
                        {!collapsed && active && (
                          <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                        )}
                      </Link>
                    )}
                    
                    {/* Child Items (Nested) */}
                    {hasChildren && !collapsed && isExpanded && (
                      <div className="ml-6 mt-0.5 space-y-0.5 border-l-2 border-tf-border pl-3">
                        {item.children!.map((child) => {
                          const ChildIconComponent = child.icon ? (Icons as any)[child.icon] : Icons.Circle;
                          const childActive = child.href ? isActive(child.href) : false;
                          
                          return (
                            <Link
                              key={child.id}
                              to={child.href || '#'}
                              className={cn(
                                "flex items-center gap-2 px-2 py-1.5 rounded transition-all text-sm",
                                childActive
                                  ? "bg-tf-primary text-white font-medium"
                                  : "text-tf-text-sub hover:bg-tf-bg-pressed hover:text-tf-text"
                              )}
                            >
                              <ChildIconComponent className="w-4 h-4 flex-shrink-0" />
                              <span className="flex-1 truncate">{child.title}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Toggle button */}
      <div className="p-2 border-t border-tf-border">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-tf-text hover:bg-tf-bg-pressed transition-all text-sm font-medium"
        >
          {collapsed ? (
            <Icons.ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <Icons.ChevronLeft className="w-4 h-4" />
              <span>Collapse</span>
            </>
          )}
        </button>
        {/* Version - Mostrado de forma delicada */}
        {version && !collapsed && (
          <div className="mt-2 pt-2 border-t border-tf-border">
            <p className="text-[10px] text-tf-text-muted text-center font-light tracking-wide">
              v{version}
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}









